/**
 * @fileoverview Legend row above the capability tables.
 * @description Explains the green and red cells, says where the permission list comes from (and how
 * many grants have been changed from the seeded ones), and holds the "Sales app" / "All
 * permissions" filter that narrows the module cards.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import SegmentedControl from '@/shared/components/atoms/SegmentedControl'

export type CapabilityFilter = 'used' | 'all'

interface CapabilitiesLegendProps {
  /** The permission-table version this list was read from, or '' when not known. */
  version: string
  /** How many grants differ from the seeded ones. */
  customCount: number
  /** How many permissions exist in total, for the "All permissions" label. */
  totalPermissions: number
  filter: CapabilityFilter
  onFilterChange: (filter: CapabilityFilter) => void
}

/**
 * @component
 * @param {CapabilitiesLegendProps} props - What to say and the filter's state.
 * @param {string} props.version - The permission-table version, or '' when unknown.
 * @param {number} props.customCount - Number of grants changed from the seeded ones.
 * @param {number} props.totalPermissions - Total permissions, shown in the "All permissions" label.
 * @param {CapabilityFilter} props.filter - Which filter is selected.
 * @param {(filter: CapabilityFilter) => void} props.onFilterChange - Called when the filter changes.
 */
function CapabilitiesLegend({
  version,
  customCount,
  totalPermissions,
  filter,
  onFilterChange,
}: CapabilitiesLegendProps) {
  const { t } = useTranslation()

  return (
    <section
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-0.5 pt-1 text-xs text-slate"
      data-testid="capabilities-legend"
    >
      <span className="flex items-center gap-1.5" data-testid="capabilities-legend-can">
        <i className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-success-bg text-track">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </i>
        {t('capabilities.legend.can')}
      </span>
      <span className="flex items-center gap-1.5" data-testid="capabilities-legend-cannot">
        <i className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-danger-bg text-danger">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
          </svg>
        </i>
        {t('capabilities.legend.cannot')}
      </span>

      <span className="min-w-[240px] flex-1" data-testid="capabilities-source">
        {version
          ? t('capabilities.source.withVersion', { version })
          : t('capabilities.source.default')}
        {customCount > 0 && ` ${t('capabilities.source.changesMade', { count: customCount })}`}
      </span>

      <SegmentedControl<CapabilityFilter>
        value={filter}
        onChange={onFilterChange}
        aria-label={t('capabilities.filter.label')}
        data-testid="capabilities-filter"
        options={[
          { value: 'used', label: t('capabilities.filter.salesApp') },
          { value: 'all', label: t('capabilities.filter.all', { count: totalPermissions }) },
        ]}
      />
    </section>
  )
}

export default CapabilitiesLegend
